import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, User, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
      });

      if (response.data.access_token) {
        localStorage.setItem("blackline_token", response.data.access_token);
        navigate("/admin");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.detail || "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <h1 className="font-heading font-bold text-3xl uppercase tracking-tight text-white">
            BLACKLINE <span className="font-normal">ESTIMATING</span>
          </h1>
          <p className="font-mono text-sm text-zinc-500 mt-2 uppercase tracking-widest">
            Admin Login
          </p>
        </div>

        {/* Login Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white border-4 border-white p-8"
          data-testid="login-form"
        >
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 flex items-center gap-3 text-red-700">
              <AlertCircle size={20} />
              <span className="font-body text-sm">{error}</span>
            </div>
          )}

          {/* Username */}
          <div className="mb-6">
            <label className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
              Username
            </label>
            <div className="relative">
              <User
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400"
              />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full h-12 pl-12 pr-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                placeholder="Enter username"
                data-testid="login-username"
              />
            </div>
          </div>

          {/* Password */}
          <div className="mb-8">
            <label className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
              Password
            </label>
            <div className="relative">
              <Lock
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-12 pl-12 pr-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                placeholder="Enter password"
                data-testid="login-password"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white h-14 uppercase font-bold tracking-widest border-2 border-black btn-brutal flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Signing In...
              </>
            ) : (
              <>
                <Lock size={20} />
                Sign In
              </>
            )}
          </button>
        </form>

        {/* Back to site */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="font-mono text-sm text-zinc-500 hover:text-white transition-colors uppercase tracking-widest"
          >
            ← Back to Site
          </a>
        </div>
      </div>
    </div>
  );
}
